/* eslint-disable camelcase */
import { call, cancelled, put, select, take, takeEvery } from 'redux-saga/effects'
import axios from 'axios'
import { get, last, values, pickBy, identity, omit, random } from 'lodash-es'
import { createSelector } from 'reselect'
import queryString from 'query-string'
import { normalize } from 'normalizr'

import API from '../config'
import { sortObject } from '../util/object'
import { collection, user as userSchema, comment } from '../schemas'
import { fromJS, List } from 'immutable'

import { receiveTopComments } from './comments'
import { receiveCollections } from './userContent'
import { receiveProfile } from './profile'
import { selectUser } from './user'
import { OPEN_MOD } from './mods'
import { SET_REFERRER_ID } from './referrer'
import { requestUserData } from './cardUserData'
import { getTimeInterval } from '../util/time'

import {
  REQUEST_ADVANCED_SEARCH,
  RECEIVE_ADVANCED_SEARCH,
  RESET_CARDS,
  RECEIVE_ADVANCED_SEARCH_PARAMETERS
} from './advancedSearch'
import { getHeaders } from './api'

const REQUEST_SEARCH = 'REQUEST_SEARCH'
export const RECEIVE_SEARCH = 'RECEIVE_SEARCH'
const RECEIVE_PREVIOUS_SEARCHES = 'RECEIVE_PREVIOUS_SEARCHES'
const RESET_SEARCH = 'RESET_SEARCH'
const defaultLimit = 21
const REQUEST_PREVIOUS_SEARCHES = 'REQUEST_PREVIOUS_SEARCHES'

const rootApi = `${API.apiPath}/${API.apiVersion}/`

const searchDefaults = {
  all: {
    isWithMeta: true,
    isPaginationWithOffset: true,
    keywordPropName: 'search_key',
    responsePropsName: 'hits',
    slug: 'account/search-all'
  },
  collections: {
    isPaginationWithMaxId: true,
    responsePropsName: 'all_decks',
    slug: 'deck/get-all-public-decks'
  },
  picks: {
    isPaginationWithMaxId: true,
    responsePropsName: 'picks',
    slug: 'deck/get-recent-picks',
    specific: {
      sort: {
        popular: {
          slug: 'deck/get-popular-picks'
        }
      }
    }
  },
  tags: {
    isWithMeta: true,
    keywordPropName: 'search_key',
    responsePropsName: 'tags',
    slug: 'account/tags-search'
  },
  mediaTags: {
    isWithMeta: true,
    keywordPropName: 'search_key',
    responsePropsName: 'tags',
    slug: 'account/media-tags-search'
  },
  users: {
    isPaginationWithOffset: true,
    responsePropsName: 'people',
    slug: 'account/people'
  },
  comment: {
    isPaginationWithMaxId: true,
    responsePropsName: 'comments',
    slug: 'deck/top-comments'
  },
  random: {
    isPaginationWithMaxId: true,
    responsePropsName: 'cards',
    slug: 'deck/random'
  },
  pro: {
    isPaginationWithMaxId: true,
    responsePropsName: 'cards',
    slug: 'deck/pro-content'
  }
}

const searchFlags = {
  fromTheEditor: {
    isPaginationWithMaxId: true,
    responsePropsName: 'all_decks',
    slug: 'deck/from-the-editor'
  },
  popularPicks: {
    isPaginationWithMaxId: true,
    responsePropsName: 'picks',
    slug: 'deck/get-popular-picks'
  },
  likes: {
    isPaginationWithMaxId: true,
    responsePropsName: 'cards',
    slug: 'deck/get-top-likes'
  },
  topComments: {
    isPaginationWithMaxId: true,
    responsePropsName: 'comments',
    slug: 'deck/top-comments'
  }
}

/** *************** Selectors ****************************/

export const selectSearch = getState => getState.search

const getAllPossibleSearchedTags = state => {
  const { search } = state
  const cards = state.entities.getIn(['cards', 'entities'])
  const searchedTags = search
    .get('results')
    .reduce(
      (acc, val, key) => [
        ...acc,
        ...(queryString.parse(key, { arrayFormat: 'index' }).type === 'tags' ? val.get('data') : [])
      ],
      []
    )
  const cardsTags = [...new Set(cards.reduce((acc, card) => [...acc, ...(card.get('tags') || [])], []))].map(tag => ({
    name: tag,
    cards_count: ''
  }))

  return values([...cardsTags, ...searchedTags].reduce((acc, tag) => ({ ...acc, [tag.name]: tag }), {}))
}

export const makeGetAllPossibleSearchedTags = () => {
  return createSelector(
    [getAllPossibleSearchedTags],
    tags => tags
  )
}

const getAllPossibleSearchedMediaTags = state => {
  const { search } = state
  const cards = state.entities.getIn(['cards', 'entities'])
  const searchedTags = search
    .get('results')
    .reduce(
      (acc, val, key) => [
        ...acc,
        ...(queryString.parse(key, { arrayFormat: 'index' }).type === 'mediaTags' ? val.get('data') : [])
      ],
      []
    )

  const cardsTags = [...new Set(cards.reduce((acc, card) => [...acc, ...(card.get('mediaTags') || [])], []))].map(
    tag => ({
      name: tag,
      cards_count: ''
    })
  )

  return values([...cardsTags, ...searchedTags].reduce((acc, tag) => ({ ...acc, [tag.name]: tag }), {}))
}

export const makeGetAllPossibleSearchedMediaTags = () => {
  return createSelector(
    [getAllPossibleSearchedMediaTags],
    tags => tags
  )
}

/** ******************************************************/
/** *************** Actions ****************************/

export const resetSearch = () => ({
  type: RESET_SEARCH
})

export const requestSearch = payload => {
  let {
    flag,
    keyword,
    postedBy,
    posted_by /* backward compatibility */,
    sort,
    sorting,
    tags = [],
    mediaTags = [],
    type,
    term,
    pickedBy,
    time_range,
    timeRange,
    types,
    no_pro_content,
    fromDate,
    end,
    limit,
    cards_only: cardsOnly,
    collectionThumbnails,
    referrer_id,
    sortBy,
    selectedBy,
    userNames
  } = payload
  return {
    type: REQUEST_SEARCH,
    payload: sortObject(
      pickBy(
        {
          flag,
          keyword,
          postedBy: postedBy || posted_by,
          sort,
          sorting,
          tags: [...new Set(tags)],
          mediaTags: [...new Set(mediaTags)],
          type,
          types,
          term,
          pickedBy,
          timeRange: timeRange || time_range,
          no_pro_content,
          fromDate,
          end,
          limit,
          cardsOnly,
          collectionThumbnails,
          referrer_id,
          sortBy,
          selectedBy,
          userNames
        },
        identity
      )
    )
  }
}

const getSearchParams = args => {
  const {
    defaults,
    current,
    keyword,
    tags,
    mediaTags,
    postedBy,
    sort,
    pickedBy,
    sorting,
    timeRange,
    types,
    no_pro_content,
    fromDate,
    end,
    limit,
    cardsOnly,
    collectionThumbnails,
    referrer_id,
    selectedBy,
    userNames
  } = args
  return {
    ...(defaults.isPaginationWithMaxId &&
      (current && current.get('data').size) > 0 && { max_id: current.getIn(['data', -1]) }),
    ...(!get(defaults, 'isPaginationWithMaxId') && {
      start:
        current && current.get('data').size ? Math.ceil(1 + (current && current.get('data').size) / defaultLimit) : 0
    }),
    ...(defaults.isPaginationWithOffset &&
      (current && current.get('data').size) > 0 && { start: current && current.get('data').size }),
    ...(keyword && keyword.length && keyword.length > 0 && { [defaults.keywordPropName || 'keyword']: keyword }),
    ...(tags && tags.length > 0 && { tags: tags.join(',') }),
    ...(mediaTags && mediaTags.length > 0 && { media_tags: mediaTags.join(',') }),
    ...(postedBy && Array.isArray(postedBy) ? { posted_by: postedBy.join(',') } : { posted_by: postedBy }),
    ...(types && Array.isArray(types) ? { type: types.join(',') } : { type: types }),
    ...(sort && { sort }),
    ...(pickedBy && { picked_by: pickedBy }),
    ...(timeRange && { ...getTimeInterval(timeRange) }),
    ...(defaults.slug === 'deck/random' && { seed: random(-1, 1, true) }),
    ...(sorting && { sorting }),
    ...(no_pro_content && { no_pro_content: no_pro_content }),
    ...(fromDate && { from_date: fromDate, to_date: fromDate + 86400 }),
    ...(end && { end }),
    ...(cardsOnly && { collections_only: 0 }),
    limit: limit || defaultLimit,
    collection_thumbnails: collectionThumbnails || 'squared',
    ...(referrer_id && { referrer_id }),
    ...(selectedBy && { selectedBy: selectedBy }),
    ...(userNames && { userNames: userNames })
  }
}

function* processSearch(action) {
  const { payload } = action
  const {
    flag,
    keyword,
    sort,
    tags,
    mediaTags,
    type,
    term,
    pickedBy,
    postedBy,
    sorting,
    timeRange,
    types,
    no_pro_content,
    fromDate,
    end,
    limit,
    cardsOnly,
    collectionThumbnails,
    referrer_id,
    sortBy,
    selectedBy,
    userNames
  } = payload
  const defaults = flag
    ? searchFlags[flag]
    : searchDefaults[pickedBy || sortBy === 'pickDate' ? 'picks' : type] || searchDefaults.all

  const CancelToken = axios.CancelToken
  const source = CancelToken.source()
  const search = yield select(selectSearch)
  const queryStr = queryString
    .stringify(sortObject(payload), { arrayFormat: 'index' })
    .replace('cardsOnly', 'cards_only')
  const current = search.getIn(['results', queryStr])
  const user = yield select(selectUser)
  const token = get(user, 'data.auth_token')
  let response
  try {
    const params = getSearchParams({
      defaults,
      current: current,
      keyword,
      tags,
      mediaTags,
      postedBy,
      sort,
      pickedBy,
      term,
      sorting,
      timeRange,
      types,
      type,
      no_pro_content,
      fromDate,
      end,
      limit,
      cardsOnly,
      collectionThumbnails,
      referrer_id,
      selectedBy,
      userNames
    })
    let data
    let defaultFeed = []
    params.type = type
    let proFeed = []
    // Fix hardcoded slug
    // pro content
    response = yield call([axios, 'get'], `${rootApi}${defaults.slug}`, {
      cancelToken: source.token,
      headers: getHeaders(token),
      params
    })
    if (response.data[defaults.responsePropsName].length) {
      defaultFeed = response.data[defaults.responsePropsName]
    }
    // pro content
    // if (type === 'picks' && sorting === 'recent' && response.data[defaults.responsePropsName].length <= 3) {
    //   yield put(requestSearch({ ...omit(payload, 'no_pro_content'), fromDate: fromDate - 604800 }))
    // }
    if (userNames && (userNames.length > 0 || userNames.size > 0)) {
      const result = yield call(
        [axios, 'post'],
        `${rootApi}account/people-by-username`,
        {
          usernames: userNames
        },
        {
          headers: getHeaders(token)
        }
      )
      defaultFeed = result.data.people
    }
    data = [...proFeed, ...defaultFeed]

    if (type === 'tags' || type === 'mediaTags') {
      yield put(receiveSearch({ queryObject: payload, end: data.length < defaultLimit, data }))
    } else {
      // only cards for now because it's not clear how to display other items
      // const onlyCards = defaults.isWithMeta ? data.filter(i => get(i, 'meta.doc_type') === 'card') : data
      const onlyCards = data
      const fromTheEditorCaseCards =
        flag === 'fromTheEditor'
          ? onlyCards.map(c =>
              omit({ ...c, latest_picker: c.creator_info }, [
                'latest_comment',
                'first_owner_comment',
                'referrer_comment',
                'pickers',
                'comments'
              ])
            )
          : onlyCards

      if (flag === 'topComments' || type === 'comment') {
        const normalizedData = normalize(response.data.comments, [comment])
        yield put(requestUserData(normalizedData.entities.cards))
        yield put(
          receiveSearch({
            queryObject: { type: 'comment' },
            end: response.data.comments.length < defaultLimit,
            data: response.data.comments.map(i => i.card.id),
            maxId: last(response.data.comments).id
          })
        )
        yield put(receiveTopComments({ data: normalizedData }))
      } else {
        const normalizedData = normalize(fromTheEditorCaseCards, [type === 'users' ? userSchema : collection])
        // make random feed always reload when it's selected by making type unique
        if (type === 'random') payload.type = `random-${params.seed}`
        yield put(
          receiveSearch({
            queryObject: omit(payload, ['no_pro_content', 'collectionThumbnails']),
            end: data.length < defaultLimit,
            data: type === 'users' ? data.map(i => i.id) : onlyCards.map(i => i.id),
            loading: data.size <= 3
          })
        )
        yield put(requestUserData(normalizedData.entities.cards))
        if (type === 'users') {
          yield put(receiveProfile(normalizedData))
        } else {
          yield put(receiveCollections(normalizedData))
        }
      }
    }
  } catch (e) {
    console.log('.... processSearch error: ', e)
  } finally {
    if (yield cancelled()) {
      source.cancel()
    }
  }
}

function* processPreviousSearches(action) {
  const CancelToken = axios.CancelToken
  const source = CancelToken.source()
  const user = yield select(selectUser)
  const token = get(user, 'data.auth_token')

  if (token) {
    try {
      const response = yield call([axios, 'get'], `${rootApi}account/previous_searches`, {
        cancelToken: source.token,
        headers: getHeaders(token)
      })

      if (get(response, 'status') === 200) {
        yield put(receivePreviousSearches(response.data))
      }
    } catch (e) {
    } finally {
      if (yield cancelled()) {
        source.cancel()
      }
    }
  }
}

export const receiveSearch = payload => {
  return {
    type: RECEIVE_SEARCH,
    payload
  }
}

const receivePreviousSearches = payload => {
  return {
    type: RECEIVE_PREVIOUS_SEARCHES,
    payload
  }
}

function* searchSaga() {
  yield takeEvery(REQUEST_SEARCH, processSearch)
}

export function requestPreviousSearches() {
  return {
    type: REQUEST_PREVIOUS_SEARCHES
  }
}

function* previousSearchesSaga() {
  while (true) {
    const action = yield take([OPEN_MOD, REQUEST_PREVIOUS_SEARCHES])
    if (get(action, 'payload.type') === 'search' || action.type === REQUEST_PREVIOUS_SEARCHES) {
      yield call(processPreviousSearches, action)
    }
  }
}

export const sagas = [searchSaga, previousSearchesSaga]

// ------------------------------------
// Reducer
// ------------------------------------

const initialState = fromJS({
  query: {
    object: {
      keyword: ''
    },
    string: ''
  },
  tagQuery: {
    object: {
      keyword: ''
    },
    string: ''
  },
  results: {},
  loading: false,
  recent: [],
  suggested: [],
  currentFeedId: null
})

export default function(state = initialState, { type, payload }) {
  switch (type) {
    case REQUEST_ADVANCED_SEARCH: {
      return state
        .delete(['results', 'advancedSearch', 'data'], new List())
        .setIn(['loading'], true)
        .setIn(['results', 'advancedSearch', 'type'], payload && payload.type)
    }

    case RECEIVE_ADVANCED_SEARCH: {
      const myData = Object.keys(
        payload.entities.cards || payload.entities.feeds || payload.entities.feedSections || {}
      )
      const array = ['results', 'advancedSearch', 'data']
      const previousData = state.getIn(array) || new List()
      return state.setIn(array, previousData.concat(new List(myData))).setIn(['loading'], false)
    }

    case RECEIVE_ADVANCED_SEARCH_PARAMETERS: {
      return state.setIn(['loading'], false)
    }

    case RESET_CARDS: {
      return state.setIn(['results', 'advancedSearch', 'data'], new List())
    }

    case REQUEST_SEARCH: {
      const query = payload.type === 'tags' ? 'tagQuery' : 'query'
      const newPayload = omit(payload, ['no_pro_content', 'fromDate', 'collectionThumbnails', 'referrer_id'])
      const string = queryString.stringify(newPayload, { arrayFormat: 'index' }).replace('cardsOnly', 'cards_only')
      return state
        .setIn([query, 'object'], fromJS(payload))
        .setIn([query, 'string'], string)
        .setIn(['loading'], true)
    }

    case RECEIVE_SEARCH: {
      const { queryObject, end, data, maxId } = payload
      const query = payload.queryObject.type === 'tags' ? 'tagQuery' : 'query'
      const queryStr = queryString
        .stringify(omit(queryObject, ['no_pro_content', 'fromDate', 'collectionThumbnails', 'referrer_id']), {
          arrayFormat: 'index'
        })
        .replace('cardsOnly', 'cards_only')
      return state
        .setIn([query, 'object'], fromJS(queryObject))
        .setIn([query, 'string'], queryStr)
        .setIn(['loading'], false)
        .setIn(['results', queryStr, 'end'], end)
        .setIn(['results', queryStr, 'maxId'], maxId)
        .setIn(
          ['results', queryStr, 'data'],
          (state.getIn(['results', queryStr, 'data']) || new List([]))
            .concat(data)
            .toOrderedSet()
            .toList()
        )
    }

    case RECEIVE_PREVIOUS_SEARCHES: {
      const { recent, suggested } = payload
      return state.set('recent', fromJS(recent)).set('suggested', fromJS(suggested))
    }

    case RESET_SEARCH:
    case SET_REFERRER_ID: {
      return initialState
    }

    default:
      return state
  }
}
